import { useEffect } from "react"
import ReactGADist from "react-ga4"
import { usePageContext } from "vike-react/usePageContext"
import PubSub from "pubsub-js"

// Vite bundles react-ga4 as `export default require_dist()` which returns the
// full CJS exports object { default: GA4Instance, ... } — unwrap it.
const ReactGA = ReactGADist?.default ?? ReactGADist

/**
 * Google Analytics 4 and Google Ads from the app's Tracking_* topics.
 *
 * Where the page carries the Google tag (AldooVike Head, from
 * pageContext.tracking: window.__aldooTracking), events go to it as they are.
 * An app that only knows a measurement id (pageContext.googleAnalyticsId)
 * gets the tag loaded here, as before. With Tag Manager alone (no GA4 id) the
 * ecommerce events are pushed to the dataLayer in GA4's shape for GTM's tags.
 * One way only, so nothing is counted twice.
 *
 * Topics (payloads in GA4's own names, items as GA4 items):
 *   Tracking_PageView (url) · Tracking_ViewItemList · Tracking_SelectItem ·
 *   Tracking_ViewItem · Tracking_AddToCart · Tracking_RemoveFromCart ·
 *   Tracking_ViewCart · Tracking_BeginCheckout · Tracking_AddShippingInfo ·
 *   Tracking_AddPaymentInfo · Tracking_Purchase · Tracking_AddToWishlist ·
 *   Tracking_Search · Tracking_Login · Tracking_NewAccountCreated (sign_up) ·
 *   Tracking_Event ({ name, params }: any other event)
 */

const SENT_PURCHASES = "aldoo-ga-purchases"

/** GA4 wants ISO 4217 in capitals; a value rounded to the cent. */
const money = (value) => (typeof value === "number" && Number.isFinite(value) ? Math.round(value * 100) / 100 : undefined)
const currencyOf = (currency) => (currency ? String(currency).toUpperCase() : undefined)

/** The ecommerce part of a payload, in GA4's names. */
const ecommerce = (data = {}) => {
  const out = {}
  if (data.currency) out.currency = currencyOf(data.currency)
  if (data.value !== undefined) out.value = money(data.value)
  if (Array.isArray(data.items)) out.items = data.items
  for (const key of ["item_list_id", "item_list_name", "coupon", "shipping_tier", "payment_type", "transaction_id", "tax", "shipping"]) {
    if (data[key] !== undefined && data[key] !== null && data[key] !== "") out[key] = key === "tax" || key === "shipping" ? money(data[key]) : data[key]
  }
  // the older payload names
  if (!out.item_list_name && data.itemListName) out.item_list_name = data.itemListName
  if (!out.transaction_id && data.transactionId) out.transaction_id = data.transactionId
  return out
}

/** A purchase is reported once, however often its page is shown. */
const firstReport = (transactionId) => {
  if (!transactionId) return true
  try {
    const sent = JSON.parse(localStorage.getItem(SENT_PURCHASES) || "[]")
    if (sent.includes(transactionId)) return false
    localStorage.setItem(SENT_PURCHASES, JSON.stringify([...sent, transactionId].slice(-50)))
  } catch (e) {
    // storage unavailable: reported, and not remembered
  }
  return true
}

export default function GoogleTracking({ children }) {
  const { googleAnalyticsId } = usePageContext()

  useEffect(() => {
    const tag = window.__aldooTracking || null
    // the measurement id only (an app without the tag in its head): load gtag here
    const legacyId = !tag && googleAnalyticsId ? googleAnalyticsId : null
    const gtmOnly = !tag?.ga4Id && !legacyId && !!(tag?.gtmId || window.__aldooGTM)
    if (!tag && !legacyId && !gtmOnly) return

    if (legacyId && !window.__aldooLegacyGA) {
      window.__aldooLegacyGA = true
      const isDebugHost = /^(stage-|localhost$|127\.)/.test(window.location.hostname)
      ReactGA.initialize(legacyId, { gtagOptions: { send_page_view: false, ...(isDebugHost ? { debug_mode: true } : {}) } })
    }

    const gtag = (...args) => {
      if (typeof window.gtag === "function") return window.gtag(...args)
      ;(window.dataLayer = window.dataLayer || []).push(args)
    }

    /** One event, one way: the Google tag, or the dataLayer for Tag Manager. */
    const send = (name, params = {}) => {
      if (gtmOnly) {
        const dataLayer = (window.dataLayer = window.dataLayer || [])
        if (params.items) dataLayer.push({ ecommerce: null })
        const { items, currency, value, ...rest } = params
        dataLayer.push(params.items ? { event: name, ecommerce: params } : { event: name, ...rest, ...(currency ? { currency } : {}), ...(value !== undefined ? { value } : {}) })
        return
      }
      gtag("event", name, params)
    }

    /** A Google Ads conversion (its label from the page's settings), with the buyer for enhanced conversions. */
    const adsConversion = (label, data = {}) => {
      if (!tag?.adsId || !label) return
      // a phone only as an international number (E.164): a local one cannot be matched
      const phone = data.phone ? String(data.phone).replace(/[^\d+]/g, "") : ""
      const phoneNumber = /^\+\d{8,15}$/.test(phone) ? phone : null
      if (data.email || phoneNumber) gtag("set", "user_data", { ...(data.email ? { email: String(data.email).trim().toLowerCase() } : {}), ...(phoneNumber ? { phone_number: phoneNumber } : {}) })
      gtag("event", "conversion", {
        send_to: `${tag.adsId}/${label}`,
        ...(data.value !== undefined ? { value: money(data.value) } : {}),
        ...(data.currency ? { currency: currencyOf(data.currency) } : {}),
        ...(data.transactionId ? { transaction_id: data.transactionId } : {}),
        // the account's conversion custom variable of the same tag (how it was paid: card / cash_on_delivery)
        ...(data.payment_type ? { payment_type: data.payment_type } : {}),
      })
    }

    const on = (topic, handler) => PubSub.subscribe(topic, (_, data) => handler(data || {}))
    const tokens = [
      // the title of the new page is set as it renders: read a moment later
      on("Tracking_PageView", (url) =>
        setTimeout(() => {
          const path = typeof url === "string" ? url : window.location.pathname + window.location.search
          send("page_view", { page_location: window.location.origin + path, page_path: path, page_title: document.title })
        }, 300)
      ),
      on("Tracking_ViewItemList", (data) => send("view_item_list", ecommerce(data))),
      on("Tracking_SelectItem", (data) => send("select_item", ecommerce(data))),
      on("Tracking_ViewItem", (data) => send("view_item", ecommerce(data))),
      // add to cart / begin checkout are also Ads conversions of their own (labels from the page's settings):
      // with consent denied the tag still sends cookieless pings, which Google models conversions from
      on("Tracking_AddToCart", (data) => {
        const params = ecommerce(data)
        send("add_to_cart", params)
        adsConversion(tag?.adsAddToCartLabel, { value: params.value, currency: params.currency })
      }),
      on("Tracking_RemoveFromCart", (data) => send("remove_from_cart", ecommerce(data))),
      on("Tracking_ViewCart", (data) => send("view_cart", ecommerce(data))),
      on("Tracking_BeginCheckout", (data) => {
        const params = ecommerce(data)
        send("begin_checkout", params)
        adsConversion(tag?.adsBeginCheckoutLabel, { value: params.value, currency: params.currency })
      }),
      on("Tracking_AddShippingInfo", (data) => send("add_shipping_info", ecommerce(data))),
      on("Tracking_AddPaymentInfo", (data) => send("add_payment_info", ecommerce(data))),
      on("Tracking_AddToWishlist", (data) => send("add_to_wishlist", ecommerce(data))),
      on("Tracking_Search", (data) => data.search_term && send("search", { search_term: String(data.search_term).slice(0, 100) })),
      on("Tracking_Login", (data) => send("login", { method: data.method || "email" })),
      on("Tracking_NewAccountCreated", (data) => {
        // one account, one sign_up: the checkout and the sign-in both announce a new one
        try {
          if (sessionStorage.getItem("aldoo-ga-signup")) return
          sessionStorage.setItem("aldoo-ga-signup", "1")
        } catch (e) {
          // storage unavailable: reported
        }
        send("sign_up", { method: data.method || "email" })
        adsConversion(tag?.adsSignupLabel, { email: data.email })
      }),
      on("Tracking_Purchase", (data) => {
        const params = ecommerce(data)
        if (!firstReport(params.transaction_id)) return
        if (data.origin) params.purchase_origin = data.origin
        send("purchase", params)
        adsConversion(tag?.adsPurchaseLabel, { ...data, value: params.value, currency: params.currency, transactionId: params.transaction_id })
      }),
      on("Tracking_Event", (data) => data.name && send(data.name, data.params || {})),
    ]
    return () => tokens.forEach((token) => PubSub.unsubscribe(token))
  }, [googleAnalyticsId])

  return children || null
}
